import { useRef, useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { gsap } from 'gsap';
import Home from './sections/Home';
import Shorts from './sections/Shorts';
import Music from './sections/Music';
import Video from './sections/Video';
import Social from './sections/Social';
import Groups from './sections/Groups';
import Notifications from './sections/Notifications';
import Search from './sections/Search';
import PostDetail from './sections/PostDetail';
import Settings from './sections/Settings';
import { BlogList, BlogDetail } from './sections/Blog';
import Profile from './sections/Profile';
import BottomNav from './components/BottomNav';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import { useAuth } from './providers/AuthProvider';
import { useUnreadCount } from './hooks/useNotifications';

/**
 * Guest guard — redirects authenticated users away from login/register.
 */
function GuestRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-[#0B0D10] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="relative mb-6">
            <div className="w-16 h-16 rounded-full border-2 border-[#A38BFF]/20 flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#A38BFF] to-[#7B68EE] animate-pulse" />
            </div>
            <div className="absolute inset-0 rounded-full border-2 border-[#A38BFF] border-t-transparent animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-[#F5F7FF] tracking-tight">Pulse</h1>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Map URL path segments to the primary "page" for BottomNav highlighting. */
function useCurrentPage(): string {
  const { pathname } = useLocation();
  if (pathname.startsWith('/shorts')) return 'shorts';
  if (pathname.startsWith('/music')) return 'music';
  if (pathname.startsWith('/video')) return 'video';
  if (pathname.startsWith('/social')) return 'social';
  if (pathname.startsWith('/groups')) return 'groups';
  if (pathname.startsWith('/notifications')) return 'notifications';
  if (pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/post/')) return 'post';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname.startsWith('/profile')) return 'profile';
  return 'home'; // /feed/* and / both map to home
}

function UserProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  return <Profile onBack={() => navigate(-1)} isOwnProfile={false} username={username} />;
}


function AppShell() {
  const { data: unreadCount = 0 } = useUnreadCount();
  const mainRef = useRef<HTMLDivElement>(null);
  const currentPage = useCurrentPage();
  const location = useLocation();
  const navigate = useNavigate();
  const [navHidden, setNavHidden] = useState(false);

  const handleChromeVisibility = useCallback((visible: boolean) => {
    setNavHidden(!visible);
  }, []);

  const isOverlay = currentPage === 'notifications' || currentPage === 'profile' || currentPage === 'search' || currentPage === 'post' || currentPage === 'settings' || currentPage === 'blog';

  useEffect(() => {
    if (mainRef.current) {
      gsap.fromTo(
        mainRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0B0D10] text-[#F5F7FF]">
      <main ref={mainRef} className="pb-safe">
        <Routes>
          {/* Feed routes — each feed tab gets its own URL */}
          <Route path="/" element={<Navigate to="/feed/forYou" replace />} />
          <Route path="/feed/forYou" element={<Home onOpenProfile={() => navigate('/profile')} onChromeVisibilityChange={handleChromeVisibility} />} />
          <Route path="/feed/following" element={<Home onOpenProfile={() => navigate('/profile')} onChromeVisibilityChange={handleChromeVisibility} />} />
          <Route path="/feed/trending" element={<Home onOpenProfile={() => navigate('/profile')} onChromeVisibilityChange={handleChromeVisibility} />} />
          <Route path="/feed/latest" element={<Home onOpenProfile={() => navigate('/profile')} onChromeVisibilityChange={handleChromeVisibility} />} />
          <Route path="/feed/discover" element={<Home onOpenProfile={() => navigate('/profile')} onChromeVisibilityChange={handleChromeVisibility} />} />

          {/* Section routes */}
          <Route path="/shorts" element={<Shorts />} />
          <Route path="/music" element={<Music />} />
          <Route path="/video" element={<Video />} />
          <Route path="/social" element={<Social />} />
          <Route path="/groups" element={<Groups />} />

          {/* Overlay-style routes */}
          <Route path="/post/:ulid" element={<PostDetail onBack={() => navigate(-1)} />} />
          <Route path="/settings" element={<Settings onBack={() => navigate(-1)} />} />
          <Route path="/blog" element={<BlogList onBack={() => navigate(-1)} />} />
          <Route path="/blog/:slug" element={<BlogDetail onBack={() => navigate(-1)} />} />
          <Route path="/search" element={<Search onBack={() => navigate(-1)} />} />
          <Route path="/notifications" element={<Notifications onBack={() => navigate(-1)} />} />
          <Route path="/profile" element={<Profile onBack={() => navigate(-1)} isOwnProfile={true} />} />
          <Route path="/profile/:username" element={<UserProfile />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/feed/forYou" replace />} />
        </Routes>
      </main>
      {!isOverlay && (
        <BottomNav
          currentPage={currentPage}
          unreadCount={unreadCount}
          hidden={navHidden}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      {/* Guest-only routes */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        }
      />

      {/* OAuth callback (accessible regardless of auth state) */}
      <Route path="/auth/callback/:provider" element={<OAuthCallbackPage />} />

      {/* Protected app shell */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
